import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    TextField,
    Button,
    Rating,
    Alert,
    CircularProgress,
    IconButton,
    Divider
} from '@mui/material';
import { ArrowBack, RateReview, Send } from '@mui/icons-material';
import api from '../axios/axiosInstance';

const FeedbackForm = ({ onBack }) => {
    const [feedback, setFeedback] = useState({
        overall_feedback: '',
        improvments_suggestion: '',
        rating: 0
    });
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });
    const [existingFeedback, setExistingFeedback] = useState(null);

    useEffect(() => {
        // Check if user has existing feedback
        fetchExistingFeedback();
    }, []);

    const fetchExistingFeedback = async () => {
        try {
            // This would need a GET endpoint for user's feedback
            // For now, we'll just handle the POST/PUT logic
        } catch (error) {
            console.log('No existing feedback found');
        }
    };

    const handleChange = (field, value) => {
        setFeedback(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const showAlert = (type, message) => {
        setAlert({ show: true, type, message });
        setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!feedback.overall_feedback.trim()) {
            showAlert('error', 'Please provide your overall feedback');
            return;
        }

        if (!feedback.improvments_suggestion.trim()) {
            showAlert('error', 'Please provide improvement suggestions');
            return;
        }

        if (feedback.rating === 0) {
            showAlert('error', 'Please provide a rating');
            return;
        }

        setLoading(true);

        try {
            const response = await api.post('/grades/feedback/submit/', feedback);
            showAlert('success', existingFeedback ? 'Feedback updated successfully!' : 'Thank you for your feedback!');

            // Clear form after successful submission
            setTimeout(() => {
                onBack();
            }, 2000);

        } catch (error) {
            showAlert('error', error.response?.data?.error || 'Failed to submit feedback. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: '800px', margin: '0 auto' }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={onBack} sx={{ mr: 2 }}>
                    <ArrowBack />
                </IconButton>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <RateReview sx={{ fontSize: 32, color: '#1976d2', mr: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Share Your Feedback
                    </Typography>
                </Box>
            </Box>

            {/* Alert */}
            {alert.show && (
                <Alert severity={alert.type} sx={{ mb: 3 }}>
                    {alert.message}
                </Alert>
            )}

            {/* Feedback Form */}
            <Card sx={{ borderRadius: '12px' }}>
                <CardContent sx={{ p: 4 }}>
                    <form onSubmit={handleSubmit}>
                        {/* Rating */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                How would you rate Gradely overall?
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <Rating
                                    name="rating"
                                    value={feedback.rating}
                                    onChange={(event, newValue) => handleChange('rating', newValue)}
                                    size="large"
                                    sx={{ mr: 2 }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {feedback.rating === 0 && 'Select a rating'}
                                    {feedback.rating === 1 && 'Poor'}
                                    {feedback.rating === 2 && 'Fair'}
                                    {feedback.rating === 3 && 'Good'}
                                    {feedback.rating === 4 && 'Very Good'}
                                    {feedback.rating === 5 && 'Excellent'}
                                </Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Overall Feedback */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                What do you think about Gradely?
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                                placeholder="Tell us what you like, what works well, and your overall experience..."
                                value={feedback.overall_feedback}
                                onChange={(e) => handleChange('overall_feedback', e.target.value)}
                                inputProps={{ maxLength: 400 }}
                                helperText={`${feedback.overall_feedback.length}/400 characters`}
                                sx={{ mt: 1 }}
                            />
                        </Box>

                        {/* Improvement Suggestions */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                How can we improve Gradely?
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                                placeholder="Share your suggestions for new features, improvements, or changes you'd like to see..."
                                value={feedback.improvments_suggestion}
                                onChange={(e) => handleChange('improvments_suggestion', e.target.value)}
                                inputProps={{ maxLength: 400 }}
                                helperText={`${feedback.improvments_suggestion.length}/400 characters`}
                                sx={{ mt: 1 }}
                            />
                        </Box>

                        {/* Submit Button */}
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                onClick={onBack}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                                sx={{ minWidth: 120 }}
                            >
                                {loading ? 'Submitting...' : 'Submit Feedback'}
                            </Button>
                        </Box>
                    </form>
                </CardContent>
            </Card>

            {/* Info Box */}
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: '8px' }}>
                <Typography variant="body2" color="text.secondary">
                    <strong>Privacy Note:</strong> Your feedback helps us improve Gradely.
                    We may use your suggestions to enhance our platform, but your personal information will remain confidential.
                </Typography>
            </Box>
        </Box>
    );
};

export default FeedbackForm;